import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBinFill, RiPencilLine } from "react-icons/ri";
import {
  deleteSaasMasterModule,
  fetchSaasMasterModules,
} from "../../../Redux/Thunks/saasMasterModulesThunk";
import { selectSaasMasterModulesListState } from "../../../Redux/Slices/saasMasterModulesSlice";
import { getListFromApiResponse } from "../../utils/apiListMappers";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import AddButton from "../../../Utility/AddButton/AddButton";
import FloatingTooltip from "../../../Utility/FloatingTooltip/FloatingTooltip";
import { ContentLoader } from "../../../Utility/Loader";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import SaasModuleMasterModal from "./SaasModuleMasterModal";
import DeleteConfirmModal from "../../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import PermissionDeniedModal from "../../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { showError, showSuccess } from "../../../utils/toast";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../../permissions/permissionKeys";
import "../../Clients/clients.css";

function isRowActive(row) {
  const v = row?.isActive;
  return v === true || v === 1 || v === "1";
}

function statusBadgeClass(active) {
  return active ? "bg-badge-success" : "bg-badge-secondary";
}

export default function SaasModulesMasterPanel() {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const canAdd = can(PERMISSION_KEYS.SAAS_MODULE_ADD);
  const canEdit = can(PERMISSION_KEYS.SAAS_MODULE_EDIT);
  const canDelete = can(PERMISSION_KEYS.SAAS_MODULE_DELETE);
  const showActionsColumn = canEdit || canDelete;
  const colCount = showActionsColumn ? 3 : 2;

  const { raw, loading, error } = useSelector(selectSaasMasterModulesListState);
  const [modalOpen, setModalOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState(null);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const rows = useMemo(() => {
    const list = getListFromApiResponse(raw);
    return list.map((r) => ({
      ...r,
      status: isRowActive(r) ? "Active" : "Inactive",
    }));
  }, [raw]);

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(rows, {
    defaultSortKey: "name",
    defaultSortDirection: "asc",
  });

  const load = useCallback(() => {
    void dispatch(fetchSaasMasterModules());
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setModuleToEdit(null);
    setModalOpen(true);
  };

  const requestAdd = () => {
    if (!canAdd) {
      setDeniedPermissionKey(PERMISSION_KEYS.SAAS_MODULE_ADD);
      return;
    }
    openAdd();
  };

  const openEdit = (row) => {
    setModuleToEdit(row);
    setModalOpen(true);
  };

  const requestEdit = (row) => {
    if (!canEdit) {
      setDeniedPermissionKey(PERMISSION_KEYS.SAAS_MODULE_EDIT);
      return;
    }
    openEdit(row);
  };

  const requestDelete = (row) => {
    if (!canDelete) {
      setDeniedPermissionKey(PERMISSION_KEYS.SAAS_MODULE_DELETE);
      return;
    }
    setModuleToDelete(row);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModuleToEdit(null);
  };

  const handleSaved = () => {
    void dispatch(fetchSaasMasterModules());
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    const id = moduleToDelete?.id;
    if (!id) return;
    setDeleteSubmitting(true);
    try {
      await dispatch(deleteSaasMasterModule(String(id))).unwrap();
      showSuccess("SaaS module deleted.");
      setModuleToDelete(null);
      await dispatch(fetchSaasMasterModules()).unwrap();
    } catch (e) {
      showError(getApiErrorMessage(e, "Failed to delete SaaS module."));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );
  }

  return (
    <div className="clients-page-content !bg-transparent">
      <ContentLoader loading={loading} className="min-h-[calc(100vh-14rem)]">
        <div className="flex w-full flex-col items-stretch justify-between gap-2 py-3 sm:flex-row sm:items-center">
          <Typography variant="text1" className="text-xl font-semibold text-text-primary">
            SaaS modules
          </Typography>
          {canAdd ? <AddButton onClick={requestAdd} tooltip="Add SaaS module" /> : null}
        </div>

        <div className="w-full">
          <div className="relative flex min-w-0 flex-col rounded-md bg-surface-primary shadow-sm transition-shadow">
            <div className="flex-1 p-2">
              <div className="overflow-x-auto">
                <table className="clients-table mb-0 w-full border-collapse">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <SortableHeader
                        columnKey="name"
                        label="Name"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="status"
                        label="Status"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      {showActionsColumn ? (
                        <SortableHeader
                          columnKey="action"
                          label="Actions"
                          onSort={handleSort}
                          currentSortKey={sortKey}
                          sortDirection={sortDirection}
                          sortable={false}
                          className="action-col"
                        />
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {loading ? (
                      <tr aria-hidden="true">
                        <td colSpan={colCount} className="h-60 min-h-[240px] border-0 p-0" />
                      </tr>
                    ) : sortedData.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="py-6 text-center">
                          <Typography className="text-text-secondary">
                            {canAdd
                              ? "No SaaS modules found. Add one to get started."
                              : "No SaaS modules found."}
                          </Typography>
                        </td>
                      </tr>
                    ) : (
                      sortedData.map((row) => {
                        const active = isRowActive(row);
                        return (
                          <tr key={row.id ?? row.name}>
                            <td className="align-middle text-start">
                              {canEdit ? (
                                <button
                                  type="button"
                                  className="m-0 w-full cursor-pointer border-0 bg-transparent p-0 text-left"
                                  onClick={() => requestEdit(row)}
                                  aria-label={`Edit module ${row.name}`}
                                >
                                  <Typography
                                    variant="span1"
                                    className="font-medium text-text-primary"
                                  >
                                    {row.name ?? "—"}
                                  </Typography>
                                </button>
                              ) : (
                                <Typography
                                  variant="span1"
                                  className="font-medium text-text-primary"
                                >
                                  {row.name ?? "—"}
                                </Typography>
                              )}
                            </td>
                            <td className="align-middle text-start">
                              <Typography
                                variant="span1"
                                component="span"
                                className={`badge rounded-full px-3 py-1 text-xs font-bold text-white ${statusBadgeClass(active)}`}
                              >
                                {row.status}
                              </Typography>
                            </td>
                            {showActionsColumn ? (
                              <td className="content-cell align-middle text-start">
                                <div className="flex items-center justify-end gap-2">
                                  {canEdit ? (
                                    <FloatingTooltip label="Edit">
                                      <Button
                                        type="button"
                                        size="sm"
                                        aria-label="Edit SaaS module"
                                        className="btn-warning-new !flex !h-7 !w-7 !items-center !justify-center !p-0 !font-normal !leading-none"
                                        onClick={() => requestEdit(row)}
                                      >
                                        <RiPencilLine className="text-[12px] text-white" />
                                      </Button>
                                    </FloatingTooltip>
                                  ) : null}
                                  {canDelete ? (
                                    <FloatingTooltip label="Delete">
                                      <Button
                                        type="button"
                                        size="sm"
                                        aria-label="Delete SaaS module"
                                        className="btn-danger-new !flex !h-7 !w-7 !items-center !justify-center !p-0 !font-normal !leading-none"
                                        onClick={() => requestDelete(row)}
                                      >
                                        <RiDeleteBinFill className="text-[12px] text-white" />
                                      </Button>
                                    </FloatingTooltip>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ContentLoader>

      {modalOpen ? (
        <SaasModuleMasterModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSaved={handleSaved}
          existingModule={moduleToEdit}
        />
      ) : null}

      <DeleteConfirmModal
        isOpen={!!moduleToDelete}
        onClose={() => !deleteSubmitting && setModuleToDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
        title="Delete SaaS module"
        itemName={moduleToDelete ? String(moduleToDelete.name ?? "") : ""}
        loading={deleteSubmitting}
      />

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
}
